// Service layer for the Admin Authentication Management module.
// Wired to the superadmin-only admin-users backend (internalapi v4/superadmin).
// Only a Super Admin can call these endpoints (enforced server-side by
// @user_must_be_wallet_superadmin); the UI additionally self-gates.

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// The full admin family — used for display (role badge labels) and list filtering,
// so existing Super Admins remain visible and filterable. Mirrors the backend
// ADMIN_MANAGED_USER_TYPES source of truth:
// loan-and-subscription: internalapi/views/user_type/superadmin/admin_users/constants.py
export const ADMIN_USER_TYPE_OPTIONS = [
  { value: "wallet_ops", label: "Wallet Ops" },
  { value: "wallet_support", label: "Wallet Support" },
  { value: "wallet_finance", label: "Wallet Finance" },
  { value: "wallet_superadmin", label: "Super Admin" },
];

// The subset a Super Admin may *create*. Excludes wallet_superadmin — a Super Admin
// cannot mint another Super Admin from the panel (mirrors the backend
// ADMIN_CREATABLE_USER_TYPES allow-list, which is the enforced boundary).
export const ADMIN_CREATABLE_USER_TYPE_OPTIONS = ADMIN_USER_TYPE_OPTIONS.filter(
  (o) => o.value !== "wallet_superadmin"
);

export const labelFor = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "—";

export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// Normalises the backend's several error shapes into a single human string:
//  - [{key, value}]  (invalid_json serializer errors)
//  - "plain string"  (e.g. "Admin user not found", "Cannot deactivate…")
//  - {status: "…"}   (auth-decorator access errors)
const parseError = (errorText) => {
  try {
    const parsed = JSON.parse(errorText);
    const msg = parsed?.msg;
    if (Array.isArray(msg)) {
      return msg.map((m) => m.value).filter(Boolean).join(" ") || errorText;
    }
    if (typeof msg === "string") return msg;
    return msg?.status || msg?.error || errorText;
  } catch {
    return errorText || "Unknown API error";
  }
};

const makeRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("No authentication token found. Please login first.");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: options.method || "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: options.body,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(parseError(await response.text()));
    }
    return response.json();
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("Request timeout. Please try again.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const ADMIN_USERS_BASE = "/internal/apis/v4/superadmin/admin-users";

// GET admin-users/list/ — search + user_type/is_active/user_role filters + ordering + pagination
export const listAdminUsers = async (params = {}) => {
  const q = new URLSearchParams();
  q.append("page", String(params.page || 1));
  q.append("items", String(params.items || 20));
  if (params.search?.trim()) q.append("search", params.search.trim());
  if (params.user_type?.trim()) q.append("user_type", params.user_type.trim());
  if (params.is_active !== undefined && params.is_active !== "") {
    q.append("is_active", String(params.is_active));
  }
  if (params.user_role?.trim()) q.append("user_role", params.user_role.trim());
  if (params.ordering?.trim()) q.append("ordering", params.ordering.trim());
  return makeRequest(`${ADMIN_USERS_BASE}/list/?${q.toString()}`);
};

// POST admin-users/create/
export const createAdminUser = async (payload) =>
  makeRequest(`${ADMIN_USERS_BASE}/create/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// POST admin-users/{id}/update-status/  { is_active }
export const updateAdminUserStatus = async (userId, isActive) =>
  makeRequest(`${ADMIN_USERS_BASE}/${userId}/update-status/`, {
    method: "POST",
    body: JSON.stringify({ is_active: isActive }),
  });
